import {Observer} from "mobx-react";
import {useRef, useState} from "react";
import styled from "styled-components";
import SideBarIcon from "../icons/sidebar.svg?react";
import {useAppStore} from "../../../StateManager";
import type {Portfolio} from "../../../data/models/Portfolio";
import {XyiconFeature} from "../../../generated/api/base";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {IconButtonV5} from "../interaction/IconButtonV5";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {colorPalette} from "../styles/colorPalette";
import {baseDistance, fontSize, radius} from "../styles/styles";
import {NavigationEnum} from "../../../Enums";
import {LineStyled, ProjectNameStyled, PortfolioNameStyled} from "./ProjectName.styled";

export const ProjectNameV5 = () => {
	const appState = useAppStore((state) => state.appState);
	const navBarState = useAppStore((state) => state.navBarState);
	const {localStorage: ls} = appState.app.transport.services;
	const setNavBarState = useAppStore((state) => state.setNavBarState);
	const dockNavBar = () => {
		setNavBarState("docked");

		ls.set(ls.getV5NavigationPanelDockedStateKey(appState.user.id), "docked");
	};

	const ref = useRef<HTMLDivElement | null>(null);
	const [tooltipVisible, setTooltipVisible] = useState(false);
	const [tooltipPos, setTooltipPos] = useState({top: 0, left: 0});

	const showTooltip = () => {
		if (!ref.current) {
			return;
		}
		const rect = ref.current.getBoundingClientRect();

		// calculate once — relative to the hovered PortfolioNameStyled
		setTooltipPos({
			top: rect.top + 48,
			left: rect.left + 100,
		});

		setTooltipVisible(true);
	};

	const portfolios = appState.actions
		.getList<Portfolio>(XyiconFeature.Portfolio)
		.toSorted((a: Portfolio, b: Portfolio) => StringUtils.sortIgnoreCase(a.name, b.name));

	return (
		<Observer>
			{() => {
				const selectedPortfolio = portfolios.find((p) => p.id === appState.activePortfolioId);
				const isOrgWide = [
					NavigationEnum.PORTFOLIOS,
					NavigationEnum.CATALOG,
					NavigationEnum.REPORTS,
					NavigationEnum.SETTINGS,
					NavigationEnum.HOME,
				].includes(appState.selectedMenu);
				const isInSettingsView = [NavigationEnum.SETTINGS].includes(appState.selectedMenu);

				return (
					<ProjectNameStyled
						className="dockIcon"
						$isInSettingsView={isInSettingsView}
						ref={ref}
					>
						{!["docked", "hidden"].includes(navBarState) && (
							<IconButtonV5
								IconComponent={SideBarIcon}
								onClick={dockNavBar}
							/>
						)}
						{isOrgWide ? (
							<div className="projectTopBar">
								<span className="projectNameTopBar">{isOrgWide ? appState.organization?.name : selectedPortfolio?.name}</span>
							</div>
						) : (
							<>
								{
									<PortfolioNameStyled
										className="projectNameTopBar"
										$label={selectedPortfolio?.name}
										onMouseEnter={showTooltip}
										onMouseLeave={() => setTooltipVisible(false)}
									>
										{selectedPortfolio?.name}
									</PortfolioNameStyled>
								}
								<LineStyled />
								{tooltipVisible && (
									<DomPortal destination={appState.app.modalContainer}>
										<TooltipTextStyled
											style={{
												position: "fixed",
												top: tooltipPos.top,
												left: tooltipPos.left,
												transform: "translateY(-8px)", // small gap above
											}}
										>
											{selectedPortfolio?.name}
										</TooltipTextStyled>
									</DomPortal>
								)}
							</>
						)}
					</ProjectNameStyled>
				);
			}}
		</Observer>
	);
};

const TooltipTextStyled = styled.div`
	background-color: ${colorPalette.blueGray.c200Light};
	color: ${colorPalette.gray.c950};
	padding: ${baseDistance.xs} ${baseDistance.xs};
	height: 24px;
	align-items: center;
	border-radius: ${radius.sm};
	white-space: nowrap;
	font-size: ${fontSize.md};
	z-index: 999999;
`;
